import * as R from 'remeda';
import { GridFilterItem } from '@rossum/ui/x-data-grid-pro';
import { detailsColumnOptionKeys } from './constants';
import { parseMqlCondition } from './helpers';
import { detailsOperators } from './operators';
import { ColumnDecoder, ColumnEncoder } from './types';

/**
 * `details` is one column in the grid. The user picks options in it, so its
 * value is a list like `['emails', 'attachments']`. The query, however, stores
 * one condition per picked option, and every option uses its own field name:
 *
 *   emails      → { email_thread:    { $emptyOrMissing: false } }
 *   attachments → { relations__type: { $eq: 'attachment' } }
 *   has_note    → { has_note:        { $eq: true } }
 *
 * Those field names exist only in the query — there is no `email_thread` column,
 * `MetaField` entry or field type for it. Hence the `ColumnEncoder` /
 * `ColumnDecoder` pair below: the generic hooks `encodeMqlQuery` /
 * `decodeMqlQuery` offer to any column stored in a shape of its own.
 */

// The column offers the user exactly two choices: the document has the picked
// option, or it does not. Both are grid operators, and everything below is
// expressed in terms of them.
type DetailsOperator = (typeof detailsOperators)[number]['value'];

/**
 * What one option looks like as a query condition, described in both
 * directions at once: `encode` writes the condition, `decode` reads it back.
 * Keeping the pair together (the value codecs in `transformers.ts` do the same)
 * means a change to one direction is made right next to the other, instead of
 * in a second file where it can be forgotten.
 */
type DetailsCondition = {
  /** the field name this option is stored under in the query */
  field: string;
  /** grid → query: what to put after the field name, e.g. `{ $eq: true }` */
  encode: (operator: DetailsOperator) => Record<string, unknown>;
  /** query → grid: which of the two operators this condition means, or null if the condition is not this option at all */
  decode: (mqlOperator: string, value: unknown) => DetailsOperator | null;
};

// Most options compare a field to a fixed value: `{ has_note: { $eq: true } }`,
// `{ relations__type: { $eq: 'attachment' } }`. They differ only in the field
// name and the value, so they share this builder. `$eq` means the document has
// the option, `$ne` means it does not.
const equality = (field: string, matchValue: boolean | string) => ({
  field,
  encode: (operator: DetailsOperator) => ({
    [operator === 'isAnyOf' ? '$eq' : '$ne']: matchValue,
  }),
  decode: (mqlOperator: string, value: unknown) => {
    if (value !== matchValue) return null;
    if (mqlOperator === '$eq') return 'isAnyOf';
    if (mqlOperator === '$ne') return 'notIn';
    return null;
  },
});

const flag = (field: string, matchValue = true) => equality(field, matchValue);

const relation = (relationType: 'attachment' | 'duplicate' | 'edit') =>
  equality('relations__type', relationType);

// The `emails` option is not a comparison — it asks whether the field is empty.
// Watch the inversion: "the document has emails" means `email_thread` is NOT
// empty, so `isAnyOf` becomes `$emptyOrMissing: false`.
const presence = (field: string) => ({
  field,
  encode: (operator: DetailsOperator) => ({
    $emptyOrMissing: operator === 'notIn',
  }),
  decode: (mqlOperator: string, value: unknown) => {
    if (mqlOperator !== '$emptyOrMissing') return null;
    if (value === false) return 'isAnyOf';
    if (value === true) return 'notIn';
    return null;
  },
});

const detailsConditions = {
  automated: flag('automated'),
  automatically_rejected: flag('automatically_rejected'),
  emails: presence('email_thread'),
  attachments: relation('attachment'),
  duplicates: relation('duplicate'),
  edit: relation('edit'),
  einvoice: flag('einvoice'),
  has_note: flag('has_note'),
  has_suggested_edit: flag('has_suggested_edit'),
  excluded_from_training: flag('training_enabled', false),
} satisfies Record<keyof typeof detailsColumnOptionKeys, DetailsCondition>;

type DetailsOptionKey = keyof typeof detailsConditions;

const isDetailsOptionKey = (value: string): value is DetailsOptionKey =>
  value in detailsConditions;

export const detailsEncoder: ColumnEncoder = ({ operator, value }) => {
  if (!Array.isArray(value)) return null;

  const detailsOperator: DetailsOperator =
    operator === 'isAnyOf' ? 'isAnyOf' : 'notIn';

  return value.flatMap(optionKey => {
    if (typeof optionKey !== 'string' || !isDetailsOptionKey(optionKey))
      return [];

    const { field, encode } = detailsConditions[optionKey];

    return [{ [field]: encode(detailsOperator) }];
  });
};

/**
 * Which option a single condition came from, and which of the two operators it
 * means. Returns null if the condition has nothing to do with `details`.
 */
const matchDetailsCondition = (condition: Record<string, unknown>) => {
  const parsed = parseMqlCondition(condition);
  if (!parsed) return null;

  const { field, mqlOperator, value } = parsed;

  // Three options (`attachments`, `duplicates`, `edit`) all use the field
  // `relations__type` and are told apart only by the value, so every option
  // using this field is tried until one of them fits.
  const matches = R.entries(detailsConditions).flatMap(
    ([optionKey, detailsCondition]) => {
      if (detailsCondition.field !== field) return [];

      const operator = detailsCondition.decode(mqlOperator, value);

      return operator ? [{ optionKey, operator }] : [];
    }
  );

  return matches[0] ?? null;
};

/**
 * The opposite of `detailsEncoder`. The encoder writes one condition per picked
 * option, so this reads all of them and puts the options back into a single
 * list: `{ email_thread: … }` plus `{ relations__type: … }` become one item with
 * the value `['emails', 'attachments']`.
 *
 * Conditions belonging to other columns are returned untouched, so the caller
 * can decode them the usual way.
 */
export const detailsDecoder: ColumnDecoder = conditions => {
  const matched = conditions.map(condition => ({
    condition,
    match: matchDetailsCondition(condition),
  }));

  // One item per operator, skipping operators that matched nothing. In the grid
  // a user picks one operator for the column, so normally only one item is made.
  const items: Omit<GridFilterItem, 'field'>[] = detailsOperators.flatMap(
    ({ value: operator }) => {
      const value = R.pipe(
        matched,
        R.flatMap(({ match }) =>
          match?.operator === operator ? [match.optionKey] : []
        ),
        R.unique()
      );

      return value.length > 0 ? [{ operator, value }] : [];
    }
  );

  return {
    items,
    unclaimedConditions: matched.flatMap(({ condition, match }) =>
      match ? [] : [condition]
    ),
  };
};
